import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Warning, ShieldCheck, Prohibit } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Pager } from "@/components/Pager";

const riskColor = { LOW: "text-primary bg-primary/10", MEDIUM: "text-amber-600 bg-amber-500/10", HIGH: "text-destructive bg-destructive/10" };

export default function AdminFraud() {
  const [list, setList] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("open");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE = 10;

  const load = () => { setError(false); api.get("/admin/fraud-flags", { params: { ...(filter === "all" ? {} : { status: filter }), limit: PAGE, offset: page * PAGE } })
    .then((r) => { setList(r.data.items); setTotal(r.data.total); }).catch(() => { setError(true); setList([]); }); };
  useEffect(() => { load(); }, [filter, page]); // eslint-disable-line
  useEffect(() => { setPage(0); }, [filter]);

  const resolve = async (id) => {
    try { await api.post(`/admin/fraud-flags/${id}/resolve`); toast.success("Flag reviewed"); load(); }
    catch { toast.error("Failed"); }
  };

  const approve = async (id) => {
    if (!window.confirm("Approve this blocked transaction and award the points?")) return;
    try { const { data } = await api.post(`/admin/fraud-flags/${id}/approve`); toast.success(`Approved — ${data.points_awarded} points awarded`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const blockedCount = (list || []).filter((f) => f.action === "auto_blocked").length;

  return (
    <div className="space-y-5">
      <h1 className="font-display font-bold text-3xl flex items-center gap-2"><Warning size={26} className="text-amber-600" /> Fraud & risk</h1>
      <p className="text-sm text-muted-foreground">HIGH-risk transactions are <span className="text-destructive font-medium">auto-blocked</span> (no points awarded) and queued here for review. Lower-risk activity is flagged.</p>

      <div className="flex items-center gap-2">
        {["open", "reviewed", "all"].map((s) => (
          <button key={s} data-testid={`fraud-filter-${s}`} onClick={() => setFilter(s)}
            className={`text-xs font-medium rounded-full px-3 py-1.5 border capitalize ${filter === s ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-black/10 hover:bg-black/5"}`}>{s}</button>
        ))}
        {blockedCount > 0 && <span className="ml-auto text-xs text-destructive flex items-center gap-1"><Prohibit size={14} /> {blockedCount} blocked in view</span>}
      </div>

      <div className="space-y-3">
        {!list && <p className="text-muted-foreground">Loading…</p>}
        {error && (
          <div className="card-surface p-8 text-center space-y-3" data-testid="fraud-error">
            <p className="text-muted-foreground text-sm">Couldn't load the review queue.</p>
            <Button onClick={load} variant="outline" className="rounded-xl border-black/10">Retry</Button>
          </div>
        )}
        {!error && list && list.length === 0 && (
          <div className="card-surface p-8 text-center text-muted-foreground text-sm flex flex-col items-center gap-2">
            <ShieldCheck size={32} className="text-primary" /> Nothing to review.
          </div>
        )}
        {!error && list && list.map((f) => (
          <div key={f.id} data-testid="fraud-row" className="card-surface p-5 flex items-center gap-4 flex-wrap">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${riskColor[f.risk]}`}>{f.risk}</span>
            {f.action === "auto_blocked" && <span data-testid="blocked-badge" className="text-xs font-bold px-3 py-1 rounded-full text-destructive bg-destructive/10 flex items-center gap-1"><Prohibit size={13} /> BLOCKED</span>}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{f.reason}</p>
              <p className="text-xs text-muted-foreground">{f.business_name} · {f.customer_name} · {fmtDateTime(f.created_at)}</p>
            </div>
            <span className="text-xs text-muted-foreground capitalize">{f.status}</span>
            {f.status === "open" && f.action === "auto_blocked" && <Button size="sm" data-testid="approve-flag" onClick={() => approve(f.id)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Approve & award</Button>}
            {f.status === "open" && <Button size="sm" data-testid="resolve-flag" onClick={() => resolve(f.id)} variant="outline" className="rounded-full border-black/10">Mark reviewed</Button>}
          </div>
        ))}
      </div>
      <Pager page={page} total={total} pageSize={PAGE} onPage={setPage} />
    </div>
  );
}
